/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { get } from 'lodash';
import { Ref } from 'react';

import { SkeletonBlock, SkeletonLoader } from '@zapier/design-system';
import {
  ChangeType,
  Currency,
  PlanStack,
  PlanV2,
  Size,
} from '@zapier/design-system-revenue/src/types';
import { isolateComponent, responsiveStyles } from '@zapier/common-utils';

import { PlanCard } from './PlanCard';

type PlanCardCollectionProps = {
  currency: Currency;
  currentPlan?: PlanV2;
  onPurchaseNow?: (plan: PlanV2) => void;
  onSelect: (selectedPlan: PlanV2, planChangeType: ChangeType) => void;
  plans: PlanStack[];
  isOnLegacyPlan: boolean;
  scrollTargetRef: Ref<any>;
  size: Size;
};

const Styles = {
  root: (size: Size) => [
    css`
      display: grid;
      grid-gap: 10px 20px;
      margin: 0 auto;
      max-width: 1200px;
      position: relative;
      z-index: 1;
    `,
    responsiveStyles(size, {
      large: css`
        grid-template-columns: repeat(5, 1fr);
      `,
      medium: css`
        grid-template-columns: repeat(3, 1fr);
      `,
      small: css``,
    }),
  ],
};

const getTooltipPosition = (size: Size, planCardIndex: number) => {
  const isFarLeftColumn =
    size === 'small' ||
    (size === 'medium' && planCardIndex === 3) ||
    planCardIndex === 0;

  const isFarRightColumn =
    size === 'small'
      ? false
      : size === 'medium'
      ? planCardIndex === 2
      : planCardIndex === 4;

  return isFarLeftColumn
    ? 'northwest'
    : isFarRightColumn
    ? 'northeast'
    : 'north';
};

/**
 * A collection of `PlanCard` components that shows the user pricing and feature information about each available plan.
 */
export const PlanCardCollection = ({
  currency,
  currentPlan,
  isOnLegacyPlan,
  onSelect,
  plans,
  scrollTargetRef,
  size,
}: PlanCardCollectionProps) => {
  const planCards = plans.map((planStack, index) => {
    return (
      <PlanCard
        currency={currency}
        currentPlan={currentPlan}
        isOnLegacyPlan={isOnLegacyPlan}
        key={`${planStack.planType}_${index}`}
        onSelect={onSelect}
        planStack={planStack}
        previousPlan={
          index > 0 ? get(plans, [index - 1, 'plans', 0]) : undefined
        }
        scrollTargetRef={scrollTargetRef}
        size={size}
        tooltipPosition={getTooltipPosition(size, index)}
      />
    );
  });

  return planCards?.length === 0 ? (
    <SkeletonLoader>
      <SkeletonBlock height={415} width="100%" />
    </SkeletonLoader>
  ) : (
    <div css={Styles.root(size)}>{planCards}</div>
  );
};
